import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, MapPin, Trophy, Check } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { CONFIG, UNITS } from "@/staticData";
import { waLink } from "@/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import BookingSection from "@/components/BookingSection";

const CONTENT = {
  ro: {
    eyebrow: "EVENIMENT MAJOR · PRE-REZERVĂRI DESCHISE",
    title: "EUROPE COMES TO RÂȘNOV.",
    subtitle: "EYOF / FOTE Brașov 2027 · 14–21 februarie 2027",
    lead: "Festivalul Olimpic al Tineretului European vine în Brașov — iar Râșnov este localitate-gazdă oficială, confirmată de COSR și Comitetul Olimpic European. Baza de sărituri cu schiurile din Valea Cărbunării găzduiește probele de sărituri. Sportivi tineri din toată Europa, la câteva minute de casa ta.",
    sports_title: "Probele FOTE 2027",
    sports: ["Schi alpin", "Snowboard", "Biatlon", "Patinaj", "Hochei", "Sărituri cu schiurile — chiar aici, în Râșnov"],
    pack_title: "Pachetul de pre-rezervare Imperfect House",
    pack: [
      "4 camere cu pat king-size 180 × 200 și baie proprie",
      "Casa întreagă pentru echipe și staff — până la 12 persoane",
      "Mic dejun la cerere, cu produse locale",
      "La câteva minute de Trambulina Valea Cărbunării",
      "Grădină, foc de seară și liniște după competiție",
      "Confirmare rapidă pe WhatsApp — fără plată online",
    ],
    motto: "Stay close to the action. Sleep away from the noise.",
    cta: "Pre-rezervă pe WhatsApp",
    wa_msg: "Bună! Vreau să pre-rezerv pentru FOTE / EYOF Brașov 2027 (14–21 februarie 2027). Suntem __ persoane, interesați de __ camere / casa întreagă.",
    back: "Înapoi la site",
    verified: "Informații verificate la 11.09.2026 · Sursa: COSR / EOC",
  },
  en: {
    eyebrow: "MAJOR EVENT · PRE-BOOKINGS OPEN",
    title: "EUROPE COMES TO RÂȘNOV.",
    subtitle: "EYOF / FOTE Brașov 2027 · 14–21 February 2027",
    lead: "The European Youth Olympic Festival comes to Brașov — and Râșnov is an official host city, confirmed by the Romanian Olympic Committee and the European Olympic Committees. The Valea Cărbunării ski-jumping base hosts the ski jumping events. Young athletes from all over Europe, minutes from your home base.",
    sports_title: "EYOF 2027 disciplines",
    sports: ["Alpine skiing", "Snowboarding", "Biathlon", "Skating", "Hockey", "Ski jumping — right here, in Râșnov"],
    pack_title: "The Imperfect House pre-booking package",
    pack: [
      "4 rooms with 180 × 200 king-size beds and private bathrooms",
      "Entire house for teams and staff — up to 12 guests",
      "Breakfast on request, with local products",
      "Minutes from the Valea Cărbunării ski jump",
      "Garden, evening fire and quiet after competition days",
      "Fast confirmation on WhatsApp — no online payment",
    ],
    motto: "Stay close to the action. Sleep away from the noise.",
    cta: "Pre-book on WhatsApp",
    wa_msg: "Hello! I'd like to pre-book for EYOF / FOTE Brașov 2027 (14–21 February 2027). We are __ people, interested in __ rooms / the entire house.",
    back: "Back to the site",
    verified: "Information verified on 11.09.2026 · Source: COSR / EOC",
  },
  de: {
    eyebrow: "GROSSEVENT · VORRESERVIERUNGEN OFFEN",
    title: "EUROPE COMES TO RÂȘNOV.",
    subtitle: "EYOF / FOTE Brașov 2027 · 14.–21. Februar 2027",
    lead: "Das Europäische Olympische Jugendfestival kommt nach Brașov — und Râșnov ist offizieller Austragungsort, bestätigt vom Rumänischen Olympischen Komitee und den Europäischen Olympischen Komitees. Die Skisprunganlage Valea Cărbunării beherbergt die Skisprung-Wettkämpfe. Junge Athleten aus ganz Europa, nur wenige Minuten von deinem Zuhause auf Zeit.",
    sports_title: "EYOF 2027 Disziplinen",
    sports: ["Ski Alpin", "Snowboard", "Biathlon", "Eislauf", "Hockey", "Skispringen — genau hier, in Râșnov"],
    pack_title: "Das Imperfect House Vorreservierungs-Paket",
    pack: [
      "4 Zimmer mit King-Size-Betten 180 × 200 und eigenem Bad",
      "Ganzes Haus für Teams und Staff — bis zu 12 Personen",
      "Frühstück auf Anfrage, mit lokalen Produkten",
      "Nur wenige Minuten von der Schanze Valea Cărbunării",
      "Garten, Abendfeuer und Ruhe nach den Wettkampftagen",
      "Schnelle Bestätigung per WhatsApp — ohne Online-Zahlung",
    ],
    motto: "Stay close to the action. Sleep away from the noise.",
    cta: "Per WhatsApp vorreservieren",
    wa_msg: "Hallo! Ich möchte für EYOF / FOTE Brașov 2027 (14.–21. Februar 2027) vorreservieren. Wir sind __ Personen, interessiert an __ Zimmern / dem ganzen Haus.",
    back: "Zurück zur Seite",
    verified: "Informationen geprüft am 11.09.2026 · Quelle: COSR / EOC",
  },
  it: {
    eyebrow: "GRANDE EVENTO · PRE-PRENOTAZIONI APERTE",
    title: "EUROPE COMES TO RÂȘNOV.",
    subtitle: "EYOF / FOTE Brașov 2027 · 14–21 febbraio 2027",
    lead: "Il Festival Olimpico della Gioventù Europea arriva a Brașov — e Râșnov è città ospitante ufficiale, confermata dal COSR e dai Comitati Olimpici Europei. La base di salto con gli sci di Valea Cărbunării ospita le gare di salto. Giovani atleti da tutta Europa, a pochi minuti da casa tua.",
    sports_title: "Le discipline EYOF 2027",
    sports: ["Sci alpino", "Snowboard", "Biathlon", "Pattinaggio", "Hockey", "Salto con gli sci — proprio qui, a Râșnov"],
    pack_title: "Il pacchetto di pre-prenotazione Imperfect House",
    pack: [
      "4 camere con letto king-size 180 × 200 e bagno privato",
      "Casa intera per squadre e staff — fino a 12 persone",
      "Colazione su richiesta, con prodotti locali",
      "A pochi minuti dal trampolino Valea Cărbunării",
      "Giardino, fuoco serale e quiete dopo le gare",
      "Conferma rapida su WhatsApp — senza pagamento online",
    ],
    motto: "Stay close to the action. Sleep away from the noise.",
    cta: "Pre-prenota su WhatsApp",
    wa_msg: "Buongiorno! Vorrei pre-prenotare per EYOF / FOTE Brașov 2027 (14–21 febbraio 2027). Siamo __ persone, interessati a __ camere / casa intera.",
    back: "Torna al sito",
    verified: "Informazioni verificate l'11.09.2026 · Fonte: COSR / EOC",
  },
};

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.8, delay },
});

const DEFAULT_PREFILL = { unitType: "room", checkIn: "", checkOut: "", adults: 2, children: 0, pet: false };

export default function Fote2027() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.en;
  const units = UNITS.filter((u) => u.active);
  const wa = CONFIG.whatsapp || "40721663181";

  useEffect(() => {
    const toTop = () => {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      window.scrollTo(0, 0);
    };
    toTop();
    const raf = requestAnimationFrame(toTop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="relative" data-testid="fote-page">
      <Header config={CONFIG} />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-brown/60 hover:text-terracotta transition-colors" data-testid="fote-back">
            <ArrowLeft size={16} /> {c.back}
          </Link>

          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-start">
            <motion.div {...fadeUp()} className="lg:col-span-5">
              <button onClick={() => scrollToId("#rezervare")} data-testid="fote-poster" aria-label={c.cta}
                className="block w-full overflow-hidden rounded-3xl shadow-2xl shadow-main/20 cursor-pointer group">
                <img src="/assets/event-fote-2027.jpg" alt="EYOF / FOTE Brașov 2027 — Europe comes to Râșnov"
                  className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700" />
              </button>
              <p className="mt-4 text-[11px] text-brown/50 font-light" data-testid="fote-verified">{c.verified}</p>
            </motion.div>

            <div className="lg:col-span-7">
              <motion.p {...fadeUp(0.1)} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-terracotta flex items-center gap-3" data-testid="fote-eyebrow">
                <Trophy size={15} /> {c.eyebrow}
              </motion.p>
              <motion.h1 {...fadeUp(0.15)} className="mt-4 font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main" data-testid="fote-title">
                {c.title}
              </motion.h1>
              <motion.p {...fadeUp(0.2)} className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold text-walnut uppercase tracking-wider" data-testid="fote-dates">
                <span className="inline-flex items-center gap-2"><CalendarDays size={15} className="text-terracotta" /> {c.subtitle}</span>
                <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-sage" /> Râșnov · Trambulina Valea Cărbunării</span>
              </motion.p>
              <motion.p {...fadeUp(0.25)} className="mt-5 text-brown/85 font-light leading-relaxed max-w-2xl" data-testid="fote-lead">
                {c.lead}
              </motion.p>

              <motion.div {...fadeUp(0.3)} className="mt-8">
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-walnut">{c.sports_title}</p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="fote-sports">
                  {c.sports.map((s) => (
                    <span key={s} className="rounded-full bg-sage/20 text-green-dark text-xs font-semibold px-4 py-2">{s}</span>
                  ))}
                </div>
              </motion.div>

              <motion.div {...fadeUp(0.35)} className="mt-10 bg-cream-light border border-warm-grey/40 rounded-3xl p-7 md:p-8" data-testid="fote-package">
                <p className="font-serif text-2xl text-main">{c.pack_title}</p>
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {c.pack.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-brown/85 font-light">
                      <Check size={16} className="text-terracotta shrink-0 mt-0.5" /> {item}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 font-serif italic text-lg text-walnut">{c.motto}</p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <a href={waLink(wa, c.wa_msg)} target="_blank" rel="noreferrer" data-testid="fote-whatsapp-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream-light font-semibold px-7 py-3.5 hover:bg-terracotta/90 transition-all duration-300 text-sm">
                    {c.cta}
                  </a>
                  <button onClick={() => scrollToId("#rezervare")} data-testid="fote-calendar-cta"
                    className="inline-flex items-center gap-2 rounded-full border border-terracotta/60 text-terracotta font-semibold px-7 py-3.5 hover:bg-terracotta hover:text-cream-light transition-all duration-300 text-sm">
                    {c.subtitle}
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        <BookingSection units={units} config={CONFIG} prefill={DEFAULT_PREFILL} />
      </main>
      <Footer config={CONFIG} />
      <WhatsAppFloat config={CONFIG} />
    </div>
  );
}
